// ==========================================
// AccessRequestModel.js
// ==========================================
export class AccessRequestModel {
  /* ─────────────────────────────────────────────────────────────────────
     ALLOWED KEYS — union of:
       - fields read in AccessRequestsPanel.jsx (drawer + DataGrid columns)
       - fields written by useAccessRequests.js (approve / reject / undo)
       - extras stored by updateRequestStatus()
       - admin-request specific fields (departmentId, city, stateCode, …)
  ───────────────────────────────────────────────────────────────────── */
  static ALLOWED_KEYS = Object.freeze([
    // Identity
    "id",

    // Applicant
    "name",
    "email",
    "phoneNumber",
    "location",
    "description",

    // Company
    "company",
    "companyId",
    "departmentId",
    "departmentName",
    "city",
    "stateCode",

    // Roles
    "role",
    "requestedRole",
    "approvedRole",

    // Lifecycle
    "status",
    "requestType",
    "processedAt",
    "reason",
    "createdUid",
    "companyIdUsed",

    // Metadata
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  static ID_FIELD = "id";
  static NAME_FIELD = "name";

  static VALID_STATUSES = Object.freeze(["pending", "approved", "rejected"]);
  static VALID_ROLES    = Object.freeze(["user", "admin", "superadmin", "locationAdmin"]);
  static VALID_TYPES    = Object.freeze(["user", "admin"]);

  /** Loose email check (mirrors HTML5 input[type=email]). */
  static EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  constructor(data = {}, { isCreation = false } = {}) {
    /* ── 1. Seed defaults ─────────────────────────────────────────────── */
    for (const key of AccessRequestModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null,
          deletedFlag: false,
        };
      } else if (key === "status") {
        this.status = "pending";
      } else if (key === "requestType") {
        this.requestType = "user";
      } else {
        this[key] = null;
      }
    }

    /* ── 2. Populate ──────────────────────────────────────────────────── */
    if (isCreation) {
      const now = new Date().toISOString();

      this.id              = data.id              || null;
      this.name            = data.name            || null;
      this.email           = data.email           || null;
      this.phoneNumber     = data.phoneNumber     || null;
      this.location        = data.location        || null;
      this.description     = data.description     || null;

      this.company         = data.company         || null;
      this.companyId       = data.companyId       || null;
      this.departmentId    = data.departmentId    || null;
      this.departmentName  = data.departmentName  || "Not specified";
      this.city            = data.city            || null;
      this.stateCode       = data.stateCode       || null;

      this.role            = data.role            || data.requestedRole || "user";
      this.requestedRole   = data.requestedRole   || data.role          || "user";
      this.approvedRole    = data.approvedRole    || null;

      this.status          = data.status          || "pending";
      this.requestType     = data.requestType     || "user";
      this.processedAt     = data.processedAt     || null;
      this.reason          = data.reason          || null;
      this.createdUid      = data.createdUid      || null;
      this.companyIdUsed   = data.companyIdUsed   || null;

      this.metadata = {
        createdAt: data.metadata?.createdAt || now,
        createdBy: data.metadata?.createdBy || "system",
        updatedAt: data.metadata?.updatedAt || now,
        updatedBy: data.metadata?.updatedBy || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      /* ── Update path — copy only whitelisted keys ─────────────────── */
      for (const key of Object.keys(data)) {
        if (!AccessRequestModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(data.metadata || {}) };
        } else {
          this[key] = data[key];
        }
      }

      // Legacy top-level audit → metadata bridge
      if (data.createdAt) this.metadata.createdAt = data.createdAt;
      if (data.createdBy) this.metadata.createdBy = data.createdBy;
      if (data.updatedAt) this.metadata.updatedAt = data.updatedAt;
      if (data.updatedBy) this.metadata.updatedBy = data.updatedBy;
    }

    // Role alias symmetry (panel reads `role`, write paths may send `requestedRole`)
    if (this.requestedRole && !this.role) this.role = this.requestedRole;
    if (this.role && !this.requestedRole) this.requestedRole = this.role;
  }

  /* ─────────────────────────────────────────────────────────────────────
     VALIDATION — creation requires the identity fields the panel reads.
     Updates are partial-friendly (only require id + status if present).
  ───────────────────────────────────────────────────────────────────── */
  isValid({ partial = false } = {}) {
    // ID always required
    if (!this.id || !String(this.id).trim()) return false;

    // Partial updates (status transitions) only require status sanity
    if (partial) {
      if (this.status && !AccessRequestModel.VALID_STATUSES.includes(this.status)) {
        return false;
      }
      if (this.requestType && !AccessRequestModel.VALID_TYPES.includes(this.requestType)) {
        return false;
      }
      if (this.role && !AccessRequestModel.VALID_ROLES.includes(this.role)) {
        return false;
      }
      if (this.approvedRole && !AccessRequestModel.VALID_ROLES.includes(this.approvedRole)) {
        return false;
      }
      return true;
    }

    // Full validation
    if (!this.name || !String(this.name).trim()) return false;

    if (!this.email || !String(this.email).trim()) return false;
    if (!AccessRequestModel.EMAIL_REGEX.test(this.email)) return false;

    if (!this.status || !AccessRequestModel.VALID_STATUSES.includes(this.status)) return false;
    if (!this.requestType || !AccessRequestModel.VALID_TYPES.includes(this.requestType)) return false;

    if (this.role && !AccessRequestModel.VALID_ROLES.includes(this.role)) return false;
    if (this.approvedRole && !AccessRequestModel.VALID_ROLES.includes(this.approvedRole)) return false;

    return true;
  }

  /* ─────────────────────────────────────────────────────────────────────
     SERIALISATION
  ───────────────────────────────────────────────────────────────────── */
  toPayload({ partial = false } = {}) {
    const payload = {};

    for (const key of AccessRequestModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        // On partial writes, only emit metadata if it has real content
        if (partial) {
          const meta = {};
          for (const mk of AccessRequestModel.ALLOWED_METADATA_KEYS) {
            if (this.metadata?.[mk] !== null && this.metadata?.[mk] !== undefined) {
              meta[mk] = this.metadata[mk];
            }
          }
          if (Object.keys(meta).length > 0) payload.metadata = meta;
        } else {
          payload.metadata = {};
          for (const mk of AccessRequestModel.ALLOWED_METADATA_KEYS) {
            payload.metadata[mk] =
              this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
          }
        }
      } else if (this[key] !== undefined && this[key] !== null) {
        payload[key] = this[key];
      } else if (!partial && this[key] !== undefined) {
        // Full writes keep nulls so the doc shape is stable
        payload[key] = this[key];
      }
    }

    return payload;
  }

  toJSON(opts) {
    return this.toPayload(opts);
  }
}